import { HistoryPagedParams, PagedResponse, buildHistoryQueryString } from "../types/history";
import { Visibility } from "../types/subscription";
import httpService from "./httpService";

// Portfolios API service
export interface BasketStock {
  id: number;
  basketId: number;
  conid: number;
  weightage: number;
  weight?: number | string; // Backend transforms weightage to weight
  positionType: "LONG" | "SHORT";
  createdAt: string;
  updatedAt: string;
  // Enriched fields from instrument service
  ticker?: string;
  name?: string;
  isin?: string;
  exchange?: string;
  price?: number;
  currency?: string;
  timestamp?: string;
  source?: string;
}

export interface PortfolioBasket {
  id: number;
  portfolioId: number;
  basketId: number;
  weightage: number;
  baskets: {
    id: number;
    name: string;
    description: string | null;
    providerId: number;
    status: "ACTIVE" | "INACTIVE";
    visibility: Visibility;
    isSubscribed?: boolean;
    createdAt: string;
    updatedAt: string;
    basketStocks: BasketStock[];
  };
}

export interface PortfolioOption {
  id: number;
  portfolioId: number;
  name?: string; // User-defined name for the option
  symbol: string;
  conId: number;
  exchange: string;
  secType: string;
  chainExchange?: string;
  expiration?: string;
  strike?: number;
  right?: string; // 'C' for Call, 'P' for Put
  weightage: number;
  positionType: "LONG" | "SHORT";
  createdAt: string;
  updatedAt: string;
  // Enriched fields from instrument service
  ticker?: string;
  currency?: string;
  primaryExchange?: string;
  weight?: number;
  // Price fields from options modal
  bid?: number;
  ask?: number;
  last?: number;
  close?: number;
}

export interface PortfolioStock {
  id: number;
  portfolioId: number;
  conid: number;
  weightage: number;
  positionType: "LONG" | "SHORT";
  // Enriched fields from instrument service
  ticker?: string;
  name?: string;
  isin?: string;
  exchange?: string;
  currency?: string;
  secType?: string;
  primaryExchange?: string;
}

export interface ProviderPortfolio {
  id: number;
  name: string;
  description: string | null;
  providerId: number;
  visibility: Visibility;
  isSubscribed?: boolean;
  numberOfStocks?: number;
  numberOfOptions?: number;
  numberOfBaskets?: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  provider?: any;
  providers?: any;
  providerName?: string;
  portfolioBaskets?: PortfolioBasket[];
  portfolioOptions?: PortfolioOption[];
  portfolioStocks?: PortfolioStock[];
}

export interface Basket {
  id: number;
  name: string;
  description: string | null;
  providerId: number;
  visibility: Visibility;
  isSubscribed?: boolean;
  numberOfStocks?: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  provider?: any;
  basketStocks: BasketStock[];
}

export interface PortfoliosResponse {
  message: string;
  portfolios: ProviderPortfolio[];
  baskets: Basket[];
}

class PortfoliosService {
  constructor() {
    console.log("PortfoliosService initialized with httpService");
  }

  async getProviderPortfolios(providerId: string, enriched: boolean = false): Promise<ProviderPortfolio[]> {
    try {
      const url = enriched ? `providers/${providerId}/portfolios?enriched=true` : `providers/${providerId}/portfolios`;

      const response = await httpService.get<PortfoliosResponse>(url);
      return response.data.portfolios;
    } catch (error) {
      console.error("Portfolios fetch error:", error);
      throw error;
    }
  }

  async getAllUserPortfolios(): Promise<{ portfolios: ProviderPortfolio[]; baskets: Basket[] }> {
    try {
      console.log("Fetching all user portfolios and baskets for discovery");
      const response = await httpService.get<PortfoliosResponse>("portfolios/all");
      return {
        portfolios: response.data.portfolios,
        baskets: response.data.baskets,
      };
    } catch (error) {
      console.error("Get all user portfolios and baskets error:", error);
      throw error;
    }
  }

  async getPortfoliosForDiscovery(): Promise<any[]> {
    try {
      console.log("Fetching portfolios for discovery");
      const response = await httpService.get<{ portfolios: any[] }>("portfolios");
      return response.data.portfolios;
    } catch (error) {
      console.error("Get portfolios for discovery error:", error);
      throw error;
    }
  }

  async getBasketsForDiscovery(): Promise<any[]> {
    try {
      console.log("Fetching baskets for discovery");
      const response = await httpService.get<{ baskets: any[] }>("baskets");
      return response.data.baskets;
    } catch (error) {
      console.error("Get baskets for discovery error:", error);
      throw error;
    }
  }

  async getPortfolioById(portfolioId: number): Promise<ProviderPortfolio> {
    try {
      const response = await httpService.get<{ portfolio: ProviderPortfolio }>(`portfolios/${portfolioId}`);
      return response.data.portfolio;
    } catch (error) {
      console.error("Get portfolio by ID error:", error);
      throw error;
    }
  }

  async getBasketById(basketId: number): Promise<Basket> {
    try {
      const response = await httpService.get<{ basket: Basket }>(`baskets/${basketId}`);
      return response.data.basket;
    } catch (error) {
      console.error("Get basket by ID error:", error);
      throw error;
    }
  }



  async getProviderBasketById(
    providerId: string,
    basketId: string,
    accessScope: 'provider' | 'investor' = 'provider'
  ): Promise<Basket> {
    try {
      const suffix = accessScope === 'investor' ? '/investors' : '/providers';
      const response = await httpService.get<{ basket: Basket }>(
        `/v2/providers/${providerId}/baskets/${basketId}${suffix}`
      );
      return response.data.basket;
    } catch (error) {
      console.error("Get provider basket by ID (v2) error:", error);
      throw error;
    }
  }

  async getProviderBasketHistoryV2(
    providerId: string,
    basketId: string,
    params?: HistoryPagedParams
  ): Promise<PagedResponse<BasketHistoryEntry>> {
    try {
      const queryString = buildHistoryQueryString(params);
      const response = await httpService.get<PagedResponse<BasketHistoryEntry>>(
        `/v2/providers/${providerId}/baskets/${basketId}/history/paged${queryString}`
      );
      return response.data;
    } catch (error) {
      console.error("Get provider basket history (v2) error:", error);
      throw error;
    }
  }






  async getProviderPortfolioById(
    providerId: string,
    portfolioId: string,
    accessScope: 'provider' | 'investor' = 'provider'
  ): Promise<ProviderPortfolio> {
    try {
      const suffix = accessScope === 'investor' ? '/investors' : '/providers';
      const response = await httpService.get<{ portfolio: ProviderPortfolio }>(
        `/v2/providers/${providerId}/portfolios/${portfolioId}${suffix}`
      );
      return response.data.portfolio;
    } catch (error) {
      console.error("Get portfolio by ID (v2) error:", error);
      throw error;
    }
  }



  async updatePortfolioVisibility(
    providerId: string,
    portfolioId: string,
    visibility: Visibility
  ): Promise<ProviderPortfolio> {
    try {
      // Use 'provider' scope since visibility updates are provider-only operations
      const portfolio = await this.getProviderPortfolioById(providerId, portfolioId, 'provider');
      if (!portfolio.portfolioStocks || !portfolio.portfolioOptions || !portfolio.portfolioBaskets) {
        throw new Error("Portfolio composition missing; cannot update visibility without full payload.");
      }
      const portfolioStocks = portfolio.portfolioStocks.map(stock => {
        if (stock.conid == null || stock.weightage == null || !stock.positionType) {
          throw new Error("Portfolio stock data incomplete; cannot update visibility.");
        }
        return {
          conId: stock.conid,
          positionType: stock.positionType,
          weightage: String(stock.weightage),
        };
      });
      const portfolioOptions = portfolio.portfolioOptions.map(option => {
        const chainExchange = option.chainExchange ?? option.exchange;
        if (
          chainExchange == null ||
          option.conId == null ||
          option.expiration == null ||
          option.name == null ||
          option.positionType == null ||
          option.right == null ||
          option.secType == null ||
          option.strike == null ||
          option.symbol == null ||
          option.weightage == null
        ) {
          throw new Error("Portfolio option data incomplete; cannot update visibility.");
        }
        return {
          chainExchange,
          conId: option.conId,
          expiration: option.expiration,
          name: option.name,
          positionType: option.positionType,
          right: option.right as "C" | "P",
          secType: option.secType,
          strike: String(option.strike),
          symbol: option.symbol,
          weightage: String(option.weightage),
        };
      });
      const portfolioBaskets = portfolio.portfolioBaskets.map(basket => {
        if (basket.basketId == null || basket.weightage == null) {
          throw new Error("Portfolio basket data incomplete; cannot update visibility.");
        }
        return {
          basketId: basket.basketId,
          weightage: String(basket.weightage),
        };
      });
      return this.updatePortfolio(providerId, portfolioId, {
        name: portfolio.name,
        description: portfolio.description ?? undefined,
        visibility,
        portfolioStocks,
        portfolioOptions,
        portfolioBaskets,
      });
    } catch (error) {
      console.error("Update portfolio visibility error:", error);
      throw error;
    }
  }

  async deletePortfolio(providerId: string, portfolioId: string): Promise<void> {
    try {
      await httpService.delete(`providers/${providerId}/portfolios/${portfolioId}`);
    } catch (error) {
      console.error("Delete portfolio error:", error);
      throw error;
    }
  }



  // V2 API methods for bulk portfolio operations
  async createPortfolio(
    providerId: string,
    portfolioData: {
      name: string;
      description?: string;
      visibility?: Visibility;
      portfolioStocks: Array<{
        conId: number;
        positionType: "LONG" | "SHORT";
        weightage: string;
      }>;
      portfolioOptions: Array<{
        chainExchange: string;
        conId: number;
        expiration: string;
        name: string;
        positionType: "LONG" | "SHORT";
        right: "C" | "P";
        secType: string;
        strike: string;
        symbol: string;
        weightage: string;
      }>;
      portfolioBaskets: Array<{
        basketId: number;
        weightage: string;
      }>;
    }
  ): Promise<ProviderPortfolio> {
    try {
      console.log("Creating portfolio V2:", { providerId, portfolioData });
      const response = await httpService.post<{ newPortfolio: ProviderPortfolio }>(
        `/v2/providers/${providerId}/portfolios`,
        portfolioData
      );
      console.log("Portfolio created successfully (V2):", response.data);
      return response.data.newPortfolio;
    } catch (error) {
      console.error("Create portfolio V2 error:", error);
      throw error;
    }
  }

  async updatePortfolio(
    providerId: string,
    portfolioId: string,
    portfolioData: {
      name: string;
      description?: string;
      visibility?: Visibility;
      portfolioStocks: Array<{
        conId: number;
        positionType: "LONG" | "SHORT";
        weightage: string;
      }>;
      portfolioOptions: Array<{
        chainExchange: string;
        conId: number;
        expiration: string;
        name: string;
        positionType: "LONG" | "SHORT";
        right: "C" | "P";
        secType: string;
        strike: string;
        symbol: string;
        weightage: string;
      }>;
      portfolioBaskets: Array<{
        basketId: number;
        weightage: string;
      }>;
    }
  ): Promise<ProviderPortfolio> {
    try {
      console.log("Updating portfolio V2:", { providerId, portfolioId, portfolioData });
      const response = await httpService.put<{ updatedPortfolio: ProviderPortfolio }>(
        `/v2/providers/${providerId}/portfolios/${portfolioId}`,
        portfolioData
      );
      console.log("Portfolio updated successfully (V2):", response.data);
      return response.data.updatedPortfolio;
    } catch (error) {
      console.error("Update portfolio V2 error:", error);
      throw error;
    }
  }

  async getProviderPortfolioHistoryV2(
    providerId: string,
    portfolioId: string,
    params?: HistoryPagedParams
  ): Promise<PagedResponse<PortfolioHistoryEntry>> {
    try {
      const queryString = buildHistoryQueryString(params);
      const response = await httpService.get<PagedResponse<PortfolioHistoryEntry>>(
        `/v2/providers/${providerId}/portfolios/${portfolioId}/history/paged${queryString}`
      );
      return response.data;
    } catch (error) {
      console.error("Get provider portfolio history (v2) error:", error);
      throw error;
    }
  }
}

// History entry types for non-admin endpoints
export interface BasketHistoryEntry {
  id: number;
  basketId: number;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  payload: any;
  createdBy: string;
  createdDateUtc: string;
}

export interface PortfolioHistoryEntry {
  id: number;
  portfolioId: number;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  payload: any;
  basketHistoryMapping?: Record<number, number> | null;
  createdBy: string;
  createdDateUtc: string;
}

export const portfoliosService = new PortfoliosService();
